import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import {
  searchAvailableSkus,
  fetchAvailableLocations,
  submitManualApproval,
  fetchRecentApprovals,
  clearApprovalMessages
} from '../store/slices/stockApprovalSlice';
import { 
  clearSearchResults 
} from '../store/slices/inventorySlice';
import { 
  Package, 
  Search, 
  MapPin, 
  CheckCircle, 
  AlertTriangle,
  Plus,
  Clock,
  FileText,
  RefreshCw
} from 'lucide-react';

const DirectStockApproval: React.FC = () => {
  const dispatch = useAppDispatch();
  const { 
    availableSkus,
    availableLocations,
    recentApprovals,
    skuSearchLoading,
    skuSearchError,
    locationsLoading,
    locationsError,
    approvalLoading,
    approvalError,
    approvalSuccess,
    recentLoading,
    recentError
  } = useAppSelector((state) => state.stockApproval);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSku, setSelectedSku] = useState<any>(null);
  const [approvalForm, setApprovalForm] = useState({
    style_code: '',
    color: '',
    size: '',
    quantity: 1,
    location_type: 'bin' as 'bin' | 'rack',
    location_id: 0,
    gatepass_number: '',
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [showLocations, setShowLocations] = useState(false);

  useEffect(() => {
    dispatch(searchAvailableSkus({ search: searchTerm }));
    dispatch(fetchRecentApprovals({ limit: 50 }));
  }, [dispatch, searchTerm]);

  useEffect(() => {
    if (showLocations) {
      dispatch(fetchAvailableLocations({}));
    }
  }, [dispatch, showLocations]);

  const handleSkuSelect = (sku: any) => {
    setSelectedSku(sku);
    setApprovalForm(prev => ({
      ...prev,
      style_code: sku.style_code
    }));
    setShowLocations(true);
  };

  const handleLocationSelect = (location: any) => {
    setApprovalForm(prev => ({
      ...prev,
      location_type: location.type,
      location_id: location.id
    }));
  };
  const handleSubmitApproval = async () => {
    if (!selectedSku || !approvalForm.color || approvalForm.quantity < 1 || approvalForm.location_id < 1) {
      return;
    }

    try {
      await dispatch(submitManualApproval(approvalForm)).unwrap();
      
      // Clear any cached inventory search results so they get refreshed
      dispatch(clearSearchResults());
      
      // Reset form after successful submission
      setSelectedSku(null);
      setApprovalForm({
        style_code: '',
        color: '',
        size: '',
        quantity: 1,
        location_type: 'bin',
        location_id: 0,
        gatepass_number: '',
        notes: '',
        date: new Date().toISOString().split('T')[0]
      });
      setShowLocations(false);
      
      // Refresh recent approvals
      dispatch(fetchRecentApprovals({ limit: 50 }));
    } catch (error) {
      console.error('Failed to submit approval:', error);
    }
  };

  const clearMessages = () => {
    dispatch(clearApprovalMessages());
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Direct Stock Approval</h1>
          <p className="mt-2 text-gray-600">Approve individual stock items and assign storage locations</p>
        </div>
        <button
          onClick={() => {
            dispatch(searchAvailableSkus({ search: searchTerm }));
            dispatch(fetchRecentApprovals({ limit: 50 }));
          }}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-blue-700"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Error Display */}
      {(skuSearchError || locationsError || approvalError || recentError) && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">
                {skuSearchError || locationsError || approvalError || recentError}
              </p>
              <button
                onClick={clearMessages}
                className="mt-2 text-sm text-red-600 hover:text-red-500"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {approvalSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex items-start">
            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-green-800">
                Stock Approved Successfully
              </h3>
              <p className="text-sm mt-1 text-green-700">
                {approvalSuccess}
              </p>
            </div>
            <button
              onClick={clearMessages}
              className="ml-3 text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">Dismiss</span>
              ×
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Available SKUs */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Available Items</h2>
            <Package className="h-5 w-5 text-gray-400" />
          </div>

          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by style code or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
          </div>

          {/* SKU List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {skuSearchLoading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-600 mt-2">Loading available items...</p>
              </div>
            ) : availableSkus.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No items available for approval</p>
              </div>
            ) : (
              availableSkus.map((sku) => (
                <div
                  key={sku.style_code}
                  onClick={() => handleSkuSelect(sku)}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedSku?.style_code === sku.style_code
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{sku.style_code}</h3>
                      <p className="text-sm text-gray-600">{sku.style_name}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span>{sku.available_colors.length} colors</span>
                        <span>{sku.available_sizes.length} sizes</span>
                        {sku.is_jeans && <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">Jeans</span>}
                      </div>
                    </div>
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                      {sku.variant_count} variants
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Approval Form */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Stock Approval</h2>
            <Plus className="h-5 w-5 text-gray-400" />
          </div>

          {selectedSku ? (
            <div className="space-y-4">
              {/* Selected SKU Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <h3 className="font-medium text-blue-900">{selectedSku.style_code}</h3>
                <p className="text-sm text-blue-700">{selectedSku.style_name}</p>
              </div>

              {/* Approval Form */}
              <div className="grid grid-cols-1 gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Color *</label>
                    <select
                      value={approvalForm.color}
                      onChange={(e) => setApprovalForm(prev => ({ ...prev, color: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select Color</option>
                      {selectedSku.available_colors.map((color: string) => (
                        <option key={color} value={color}>{color}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
                    <select
                      value={approvalForm.size}
                      onChange={(e) => setApprovalForm(prev => ({ ...prev, size: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Free Size</option>
                      {selectedSku.available_sizes.map((size: string) => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                    <input
                      type="number"
                      min="1"
                      value={approvalForm.quantity}
                      onChange={(e) => setApprovalForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      value={approvalForm.date}
                      onChange={(e) => setApprovalForm(prev => ({ ...prev, date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gatepass Number</label>
                    <input
                      type="text"
                      placeholder="e.g., GP001"
                      value={approvalForm.gatepass_number}
                      onChange={(e) => setApprovalForm(prev => ({ ...prev, gatepass_number: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <input
                      type="text"
                      placeholder="Optional remarks"
                      value={approvalForm.notes}
                      onChange={(e) => setApprovalForm(prev => ({ ...prev, notes: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Location Selection */}
                {showLocations && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Storage Location *</label>
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-2">
                      {locationsLoading ? (
                        <div className="col-span-2 text-center py-4">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
                        </div>
                      ) : (
                        <>
                          {availableLocations.bins?.map((location) => (
                            <div
                              key={`bin-${location.id}`}
                              onClick={() => handleLocationSelect(location)}
                              className={`p-2 border rounded cursor-pointer text-sm ${
                                approvalForm.location_id === location.id && approvalForm.location_type === 'bin'
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center">
                                <Package className="h-3 w-3 text-blue-600 mr-1" />
                                <span className="font-medium">{location.location}</span>
                              </div>
                              <div className="text-xs text-gray-500">
                                {location.available_space}/{location.capacity} available
                              </div>
                            </div>
                          ))}
                          {availableLocations.racks?.map((location) => (
                            <div
                              key={`rack-${location.id}`}
                              onClick={() => handleLocationSelect(location)}
                              className={`p-2 border rounded cursor-pointer text-sm ${
                                approvalForm.location_id === location.id && approvalForm.location_type === 'rack'
                                  ? 'border-green-500 bg-green-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center">
                                <MapPin className="h-3 w-3 text-green-600 mr-1" />
                                <span className="font-medium">{location.location}</span>
                              </div>
                              <div className="text-xs text-gray-500">
                                {location.available_space}/{location.capacity} available
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  onClick={handleSubmitApproval}
                  disabled={
                    !selectedSku || 
                    !approvalForm.color || 
                    approvalForm.quantity < 1 || 
                    approvalForm.location_id < 1 ||
                    approvalLoading
                  }
                  className="w-full inline-flex justify-center items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {approvalLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Approving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve Stock
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>Select an item to approve</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Approvals */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">Recent Approvals</h2>
            <Clock className="h-5 w-5 text-gray-400" />
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Style</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gatepass</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentApprovals.map((approval) => (
                <tr key={approval.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(approval.date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{approval.style_code}</div>
                    <div className="text-sm text-gray-500">{approval.sku}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{approval.color}</div>
                    <div className="text-sm text-gray-500">{approval.size || 'Free Size'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {approval.quantity}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {approval.location_type === 'bin' ? (
                        <Package className="h-4 w-4 text-blue-600 mr-1" />
                      ) : (
                        <MapPin className="h-4 w-4 text-green-600 mr-1" />
                      )}
                      <span className="text-sm text-gray-900">{approval.location}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {approval.gatepass_number || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {recentApprovals.length === 0 && !recentLoading && (
            <div className="text-center py-8 text-gray-500">
              <Clock className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No recent approvals found</p>
            </div>
          )}
          
          {recentLoading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-600 mt-2">Loading recent approvals...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DirectStockApproval;
